/**
 * @flow
 */

import React, { useState, useEffect } from 'react';
import gql from 'graphql-tag';
import { format, startOfDay, addMinutes } from 'date-fns';
import { useMutation } from '@apollo/client';
import { useSelector } from 'react-redux';
import { Modal, Header, Button, Icon, Message } from 'semantic-ui-react';
import { FormattedMessage } from 'react-intl';
import type {
    ModalDialog,
    WorkingTime,
    Master,
    ScheduleSettings,
} from '../../../../type';

import { GET_SALON_QUERY } from '../../containers/schedule/timetable/Timetable';

type DeleteWorkingTimeDialogProps = {
    schedule: ScheduleSettings,
    master: Master,
    workingTime: WorkingTime,
} & ModalDialog;

// Mutation that deletes specified working time
const DELETE_WORKING_TIME_MUTATION = gql`
    mutation deleteWorkingTime($input: DeleteWorkingTimeInput!) {
        deleteWorkingTime(input: $input) {
            workingTimeId
            deleted
        }
    }
`;

const DeleteWorkingTimeDialog = ({
    schedule,
    master,
    workingTime,
    trigger,
    open,
    onClose,
}: DeleteWorkingTimeDialogProps) => {
    const salon = useSelector(({ user }) => user.get('salon'));
    const date = useSelector(({ schedule: s }) => s.get('date'));
    const showArchive = useSelector(({ salon: sal }) => sal.get('showArchive'));
    const currentLanguage = useSelector(
        ({ intl }) => intl.get('locale') || intl.get('defaultLanguage'),
    );

    const [isDeletable, setIsDeletable] = useState(true);
    const [error, setError] = useState(false);
    const [deleteWorkingTime] = useMutation(DELETE_WORKING_TIME_MUTATION);

    // The dialog stays mounted between opens (withModal toggles `open`), so
    // reset to the confirm state each time it reopens.
    useEffect(() => {
        if (open) {
            setIsDeletable(true);
            setError(false);
        }
    }, [open]);

    const onDelete = async () => {
        const variables = {
            salon,
            showArchive,
            date: format(new Date(date), 'yyyy-MM-dd'),
            lang: currentLanguage,
        };

        try {
            const {
                data: {
                    deleteWorkingTime: { deleted },
                },
            } = await deleteWorkingTime({
                variables: { input: { workingTimeId: workingTime.id } },
                // No optimistic response — act only on the server result so we
                // never remove the slot (and unmount this dialog) prematurely.
                // Remove the deleted working time from the query cache
                // (immutable rewrite — readQuery results are frozen in AC3).
                update: (store, { data: { deleteWorkingTime: result } }) => {
                    if (!result.deleted) return;
                    try {
                        const data = store.readQuery({
                            query: GET_SALON_QUERY,
                            variables,
                        });
                        if (!data) return;
                        store.writeQuery({
                            query: GET_SALON_QUERY,
                            variables,
                            data: {
                                ...data,
                                viewer: {
                                    ...data.viewer,
                                    salon: {
                                        ...data.viewer.salon,
                                        masters: {
                                            ...data.viewer.salon.masters,
                                            nodes: data.viewer.salon.masters.nodes.map(
                                                m =>
                                                    m.id === master.id
                                                        ? {
                                                              ...m,
                                                              schedule: m.schedule.filter(
                                                                  wt =>
                                                                      wt.id !==
                                                                      workingTime.id,
                                                              ),
                                                          }
                                                        : m,
                                            ),
                                        },
                                    },
                                },
                            },
                        });
                    } catch (e) {
                        // It's safe to ignore this exception
                    }
                },
            });

            if (deleted) {
                // Cache update above removed the slot, unmounting this dialog;
                // close the modal via the parent.
                onClose();
            } else {
                // Day has appointments — keep the dialog open with a message.
                setIsDeletable(false);
            }
        } catch (e) {
            setError(true);
        }
    };

    // The dialog switches from "confirm" to a "result" state once the delete
    // is blocked (day has appointments) or errors — showing only the relevant
    // message + a single Close button instead of the confirmation + Yes/No.
    const blocked = isDeletable === false || error;

    return (
        <Modal
            trigger={trigger}
            open={open}
            onClose={onClose}
            size="small"
            basic
        >
            <Header icon>
                <Icon name="remove" />
                <FormattedMessage
                    id="pages.settings.schedule.form.deleteForm.deleteHeader"
                    defaultMessage="Delete Working Time"
                />
            </Header>
            <Modal.Content>
                {blocked ? (
                    <Message
                        error={!!error}
                        icon="warning sign"
                        content={
                            error ? (
                                <FormattedMessage
                                    id="errors.defaultMessage"
                                    defaultMessage="Something went wrong"
                                />
                            ) : (
                                <FormattedMessage
                                    id="pages.settings.schedule.form.deleteForm.notDeleted"
                                    defaultMessage="Schedule cannot be deleted, because there are appointments for that day"
                                />
                            )
                        }
                    />
                ) : (
                    <FormattedMessage
                        id="pages.settings.schedule.form.deleteForm.deleteText"
                        defaultMessage="Are you sure you want to delete working time of {masterFirstName} {masterLastName} between {startWorkingAt} and {endWorkingAt}?"
                        values={{
                            masterFirstName: master.firstName,
                            masterLastName: master.lastName,
                            startWorkingAt: format(
                                addMinutes(
                                    startOfDay(new Date()),
                                    workingTime.startAt,
                                ),
                                schedule.format,
                            ),
                            endWorkingAt: format(
                                addMinutes(
                                    startOfDay(new Date()),
                                    workingTime.endAt,
                                ),
                                schedule.format,
                            ),
                        }}
                    />
                )}
            </Modal.Content>
            <Modal.Actions>
                {blocked ? (
                    <Button onClick={onClose} basic inverted>
                        <Icon name="remove" />
                        <FormattedMessage
                            id="pages.settings.schedule.form.deleteForm.close"
                            defaultMessage="Close"
                        />
                    </Button>
                ) : (
                    <React.Fragment>
                        <Button onClick={onClose} color="red" basic inverted>
                            <Icon name="remove" />
                            <FormattedMessage
                                id="pages.settings.schedule.form.deleteForm.no"
                                defaultMessage="No"
                            />
                        </Button>
                        <Button onClick={onDelete} color="green" inverted>
                            <Icon name="checkmark" />
                            <FormattedMessage
                                id="pages.settings.schedule.form.deleteForm.yes"
                                defaultMessage="Yes"
                            />
                        </Button>
                    </React.Fragment>
                )}
            </Modal.Actions>
        </Modal>
    );
};

export default DeleteWorkingTimeDialog;
